import { isB2BUser, isProduction, isSSR } from '@js/App/Utils/Environment'
import { router } from '@inertiajs/vue3'
import { useUser } from '@js/App/Api/User/User'
import { getCountry } from '@js/App/Utils/CountryAndLocale'
import { clearStoredEmailWithoutConsent, pushUserProvidedData } from '@js/App/Tracking/UserProvidedData'
import * as Sentry from '@sentry/vue'

// posthog-js is ~196KB. It is dynamically imported here (and in usePostHog.js, where it
// is init'd on marketing consent) so it stays out of the entry chunk. posthog-js is a
// singleton, so this resolves to the same instance usePostHog.js initialises.
//
// Resolves null instead of rejecting: the callers of trackFunnelStep/trackCompleteRegistration
// await them without a catch, so a failed chunk fetch (classic case: stale chunk hash right
// after a deploy) must never block the funnel's next step or registration completion.
// Without marketing consent posthog is never init'd and capture/identify no-op anyway, so
// the chunk isn't fetched at all in that case.
const getPosthog = async () => {
  if (!window.Cookiebot?.consent?.marketing) { return null }

  try {
    const { default: posthog } = await import('posthog-js')
    return posthog
  } catch (error) {
    Sentry.captureException(error)
    return null
  }
}

export const setupPageViewTracking = () => {
  if (isSSR()) { return }

  // Feed the hashed user-provided-data into the dataLayer on the initial load
  // (both immediately, for SPA re-entry where consent is already resolved, and
  // once Cookiebot reports consent) and on every subsequent navigation.
  // CookiebotOnConsentReady fires on every consent change — clear stored PII if
  // consent was revoked, otherwise (re)push for the current consent state.
  window.addEventListener('CookiebotOnConsentReady', () => {
    clearStoredEmailWithoutConsent()
    pushUserProvidedData()
  })
  pushUserProvidedData()

  let isFirstNavigate = true
  router.on('navigate', async () => {
    // Await so user-provided-data lands in the dataLayer before virtualPageview
    // fires — otherwise the enhanced-conversion tag reads the previous nav's value.
    await pushUserProvidedData()

    // For Google tracking we don't want to track the first virtual page view event, because that fires a regular
    // page view already
    if (!isFirstNavigate) {
      await trackPageView()
    }
    isFirstNavigate = false
  })
}

export const trackPurchase = async (amountInCents, invoiceId, planName, email, coupon, months, discount) => {
  if (isSSR()) { return }
  if (!isProduction()) { return }
  if (isB2BUser()) { return }
  if (!shouldTrackBasedOnLastTrackedAt('purchase')) {
    console.log('[Tracking 🕵️] Not tracking Purchase as it was already tracked in the last 5 minutes')
    return
  }

  await waitForDataLayerToBeLoaded()

  if (!window.dataLayer) {
    console.log('[Tracking 🕵️] Unable to track Purchase because window.dataLayer is undefined')
    return
  }

  console.log(`[Tracking 🕵️] Track Purchase! ${amountInCents / 100}EUR`)

  const payload = {
    currency: 'EUR',
    transaction_id: invoiceId,
    value: amountInCents / 100,
    affiliation: '',
    coupon,
    shipping: 0,
    tax: amountInCents / 100 / 1.21,
    email,
    items: [
      getTrackingPurchaseItem(planName, coupon, discount, amountInCents, months)
    ],
    rentbirdUserData: getCurrentUserTrackingDataAsObject(email)
  }

  window.gtag('event', 'purchase', payload)

  window.gtag('set', 'user_data', {
    email
  })

  window.gtag('event', 'conversion', {
    send_to: 'AW-475248539/nl4ZCNr5r4YYEJvvzuIB',
    value: amountInCents / 100,
    currency: 'EUR',
    transaction_id: invoiceId
  })
}

export const trackPageView = async () => {
  if (isSSR()) { return }
  if (isB2BUser()) { return }
  if (!isProduction()) { return }

  await waitForDataLayerToBeLoaded()

  if (!window.dataLayer) {
    console.log('[Tracking 🕵️] Unable to trackPageView because window.dataLayer is undefined')
    return
  }

  console.log('[Tracking 🕵️] Pushing virtualPageview event to dataLayer url: ' + window.location.href)
  window.dataLayer.push({
    event: 'virtualPageview',
    pageUrl: window.location.href,
    pageTitle: document.title
  })
}

export const trackFunnelStep = async (step, selectedCity = null) => {
  if (isSSR()) { return }
  if (!isProduction()) { return }
  if (isB2BUser()) { return }
  if (!shouldTrackBasedOnLastTrackedAt(`FunnelStep${step}`)) {
    console.log(`[Tracking 🕵️] Not tracking funnel step ${step} as it was already tracked in the last 5 minutes`)
    return
  }

  const { user } = useUser()
  if (user.value.isLoggedIn) {
    console.log(`[Tracking 🕵️] Not tracking funnel step ${step} because user is already logged in`)
    return
  }

  await waitForDataLayerToBeLoaded()

  console.log(`[Tracking 🕵️] Tracking funnel step ${step}`)

  const payload = {
    event_category: 'User',
    event_label: 'FunnelStep',
    value: step,
    selectedCity,
    rentbirdUserData: getCurrentUserTrackingDataAsObject()
  }

  window.gtag('event', 'FunnelStep', payload)

  const posthog = await getPosthog()
  posthog?.capture('Rentbird Funnel', {
    step,
    city: selectedCity
  })
}

export const trackClickBackInFunnel = () => {
  console.log('[Tracking 🕵️] Clicked back in funnel')
}

export const trackAddToCart = async (planName, coupon, discount, amountInCents, months) => {
  if (isSSR()) { return }
  if (isB2BUser()) { return }
  if (!isProduction()) { return }
  if (!shouldTrackBasedOnLastTrackedAt('AddToCart')) {
    console.log('[Tracking 🕵️] Not tracking AddToCart as it was already tracked in the last 5 minutes')
    return
  }

  console.log('[Tracking 🕵️] Add to cart')

  await waitForDataLayerToBeLoaded()

  const payload = {
    event_category: 'User',
    event_label: 'AddToCart',
    items: [
      getTrackingPurchaseItem(planName, coupon, discount, amountInCents, months)
    ],
    rentbirdUserData: getCurrentUserTrackingDataAsObject()
  }

  window.gtag('event', 'add_to_cart', payload)
}

export const trackInitiateCheckout = async () => {
  const { user } = useUser()
  if (isSSR()) { return }
  if (isB2BUser()) { return }
  if (!isProduction()) { return }

  if (!shouldTrackBasedOnLastTrackedAt('InitiateCheckout')) {
    console.log('[Tracking 🕵️] Not tracking InitiateCheckout as it was already tracked in the last 5 minutes')
    return
  }

  console.log('[Tracking 🕵️] InitiateCheckout, email=' + user.value.email)

  await waitForDataLayerToBeLoaded()

  const payload = {
    event_category: 'User',
    event_label: 'InitiateCheckout',
    email: user.value.email,
    rentbirdUserData: getCurrentUserTrackingDataAsObject()
  }

  window.gtag('event', 'InitiateCheckout', payload)

  // Also track this event as begin_checkout. Request from Stijn, I think it has something to do with GA4
  window.gtag('event', 'begin_checkout', payload)
}

export const trackCompleteRegistration = async (email) => {
  if (isSSR()) { return }
  if (isB2BUser()) { return }
  if (!isProduction()) { return }

  if (!shouldTrackBasedOnLastTrackedAt('CompleteRegistration')) {
    console.log('[Tracking 🕵️] Not tracking CompleteRegistration as it was already tracked in the last 5 minutes')
    return
  }

  await waitForDataLayerToBeLoaded()
  await waitForRentbirdUserDataToBeFilledIn() // Give the code time to go through the update cycle

  const rentbirdUserData = getCurrentUserTrackingDataAsObject(email)

  console.log(`[Tracking 🕵️] Tracking CompleteRegistration for user random id ${rentbirdUserData.userRandomId}`)

  const payload = {
    event_category: 'User',
    event_label: 'CompleteRegistration',
    email,
    rentbirdUserData
  }

  window.gtag('event', 'CompleteRegistration', payload)

  const posthog = await getPosthog()
  posthog?.identify(rentbirdUserData.userRandomId)
}

export const trackSubscriptionSelection = (subscription) => {
  console.log(`[Tracking 🕵️] Tracking SelectSubscription: ${subscription}`)

  if (isSSR()) { return }
  if (isB2BUser()) { return }
  if (!isProduction()) { return }

  const payload = {
    event_category: 'User',
    event_label: 'SelectSubscription',
    value: subscription,
    rentbirdUserData: getCurrentUserTrackingDataAsObject()
  }
  window.gtag('event', 'SelectSubscription', payload)
}

export const trackCouponCodeUsed = (oldPrice, newPrice) => {
  console.log(`[Tracking 🕵️] Tracking CouponCodeUsed: ${oldPrice} - ${newPrice}`)

  if (isSSR()) { return }
  if (isB2BUser()) { return }
  if (!isProduction()) { return }

  const payload = {
    event_category: 'User',
    event_label: 'CouponCodeUsed',
    oldPrice,
    newPrice,
    rentbirdUserData: getCurrentUserTrackingDataAsObject()
  }
  window.gtag('event', 'CouponCodeUsed', payload)
}

export const trackCancelSubscriptionReason = async (cancelReason) => {
  if (isSSR()) { return }
  if (isB2BUser()) { return }
  if (!isProduction()) { return }

  await waitForDataLayerToBeLoaded()

  console.log('[Tracking 🕵️] Tracking cancel subscription reason')

  const payload = {
    event_category: 'User',
    event_label: 'CancelSubscriptionReason',
    value: cancelReason,
    rentbirdUserData: getCurrentUserTrackingDataAsObject()
  }

  window.gtag('event', 'CancelSubscriptionReason', payload)
}

const waitForDataLayerToBeLoaded = () => new Promise((resolve, reject) => {
  const interval = setInterval(() => {
    if (window.dataLayer) {
      clearInterval(interval)
      clearTimeout(timeout)
      resolve()
    }
  }, 100)

  const timeout = setTimeout(() => {
    clearInterval(interval)
    reject(new Error('[DEBUG] timeout exceeded while waiting for data layer'))
  }, 5000)
})

const waitForRentbirdUserDataToBeFilledIn = () => new Promise((resolve) => {
  const interval = setInterval(() => {
    const rentbirdUserData = getCurrentUserTrackingDataAsObject()
    if (rentbirdUserData.userFirstName) {
      clearInterval(interval)
      clearTimeout(timeout)
      resolve()
    }
  }, 100)

  const timeout = setTimeout(() => {
    clearInterval(interval)
    resolve()
  }, 5000)
})

const shouldTrackBasedOnLastTrackedAt = (eventName, minTimeBetweenEventsInSeconds = 300) => {
  const lastTrackedAddToCartAtAsString = localStorage.getItem(eventName)
  if (!lastTrackedAddToCartAtAsString) {
    localStorage.setItem(eventName, `${new Date().getTime()}`)
    return true
  }

  const lastTrackedAddToCartAt = parseInt(lastTrackedAddToCartAtAsString)
  const diffInS = ((new Date()).getTime() - lastTrackedAddToCartAt) / 1000

  if (diffInS < minTimeBetweenEventsInSeconds) {
    return false
  }

  localStorage.setItem(eventName, `${new Date().getTime()}`)
  return true
}

const getTrackingPurchaseItem = (planName, coupon, discount, amountInCents, months) => ({
  item_id: planName,
  item_name: planName,
  affiliation: '',
  coupon,
  currency: 'EUR',
  discount: discount / 100,
  index: 0,
  item_brand: 'Rentbird',
  item_category: 'Abonnement',
  item_category2: '',
  item_category3: '',
  item_category4: '',
  item_category5: '',
  item_list_id: 'abonnementen',
  item_list_name: 'Abonnementen',
  item_variant: planName,
  location_id: '',
  price: amountInCents / 100 / months,
  quantity: months
})

export const getCurrentUserTrackingDataAsObject = (updatedEmail = null) => {
  const { user } = useUser()

  return {
    userRandomId: user.value.serverId,
    userEmail: user.value.email,
    userPhone: user.value.phone,
    userFirstName: user.value.firstNameFormatted,
    userLastName: user.value.lastNameFormatted,
    userNotificationChannel: user.value.notificationChannel,
    userEnabledAutoRenew: user.value.enabledAutoRenew,
    userNumberOfSearchProfiles: user.value.numberOfSearchProfiles,
    userIsLoggedIn: user.value.isLoggedIn,
    userHasMadeAtLeastOnePayment: user.value.hasMadeAtLeastOnePayment,
    userFirstPaymentCreatedAt: user.value.firstPaymentCreatedAt,
    userCountry: getCountry(),
    userHasMadeFirstPaymentOnThisDayThatIsBeforeThisEvent: user.value.hasMadeFirstPaymentToday,
    userHasMobilePhoneLinked: user.value.hasMobilePhoneLinked
  }
}

// ---------------------------------------------------------------------------
// Offboarding review funnel (RBD-1201)
//
// PostHog only — these are product events, not ad-platform conversions, so they
// deliberately skip the dataLayer/gtag path the funnel events above use.
//
// Properties carry the rating and the reason *slug* only. Never the free-text
// improvement comments or the user's email: those stay in the feedback endpoint.
// ---------------------------------------------------------------------------

const captureReviewFunnelEvent = async (event, properties = {}) => {
  if (isSSR()) { return }
  if (!isProduction()) { return }
  if (isB2BUser()) { return }

  const posthog = await getPosthog()
  posthog?.capture(event, properties)
}

export const trackReviewAskShown = (rating, foundAHouse) =>
  captureReviewFunnelEvent('review ask shown', { rating, found_a_house: foundAHouse })

export const trackReviewAskClicked = rating =>
  captureReviewFunnelEvent('review ask clicked', { rating })

// popupBlocked distinguishes "user went to Trustpilot" from "the tab never opened".
// Without it a blocked popup is indistinguishable from a successful click, because
// the flow's next button unlocks either way (see RBD-1167).
export const trackTrustpilotLinkOpened = popupBlocked =>
  captureReviewFunnelEvent('trustpilot link opened', { popup_blocked: popupBlocked })

export const trackReviewFlowCompleted = () =>
  captureReviewFunnelEvent('review flow completed')
